import { Fragment, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { BandButton, CompactBand } from '@/components/CompactBand';
import { LIVE_CHECK } from './mockData';
import { payerPathFor } from '@/features/settings-d/payerRulesMock';
import type { LiveCheckSource, LiveCheckStateData, QuotedProcedure } from './types';
import { countWord, firstName, formatCents, formatCurrency, formatDate, patientOwes, quoteTotals } from './utils';
import './livecheck.css';

const fx = LIVE_CHECK;
const r = fx.report;

/** Source names as staff read them (rcm-29 §4.7). The data keeps the type names. */
const SOURCE_LABEL: Record<LiveCheckSource, string> = {
  Stedi: 'Stedi',
  'Historical data': 'Historical data',
  'Payer Manual': 'Payer manual',
  'AI Call': 'ELVA call',
  'Payer Portal': 'Payer portal',
};

function sourceText(list: LiveCheckSource[]): string {
  return list.map((s) => SOURCE_LABEL[s]).join(' · ');
}

/** Amber when a share of the fee is covered; grey when bundled or excluded; green at 100%. */
function coverClass(covered: string): string {
  if (/bundled|not covered/i.test(covered)) return 'lcr-cov non';
  if (covered.includes('100%')) return 'lcr-cov';
  return 'lcr-cov part';
}

type GlyphKind = 'shield' | 'dial' | 'layers' | 'card' | 'warn';

function Glyph({ kind }: { kind: GlyphKind }) {
  const common = { viewBox: '0 0 24 24', 'aria-hidden': true };
  if (kind === 'shield') {
    return (
      <svg {...common}>
        <path d="M12 3.2l7 2.9v4.8c0 4.4-2.9 7.8-7 9.9-4.1-2.1-7-5.5-7-9.9V6.1z" />
        <path d="M8.6 12.3l2.4 2.4 4.4-4.8" />
      </svg>
    );
  }
  if (kind === 'dial') {
    return (
      <svg {...common}>
        <circle cx="12" cy="12" r="8.5" />
        <path d="M12 7.3v9.4" />
        <path d="M14.4 9.5c0-.9-1.1-1.5-2.4-1.5s-2.4.6-2.4 1.7 1.1 1.5 2.4 1.9 2.7.8 2.7 2-1.3 1.7-2.7 1.7-2.4-.5-2.4-1.4" />
      </svg>
    );
  }
  if (kind === 'layers') {
    return (
      <svg {...common}>
        <path d="M12 3.5l8 4-8 4-8-4z" />
        <path d="M4 12l8 4 8-4" />
        <path d="M4 16.3l8 4 8-4" />
      </svg>
    );
  }
  if (kind === 'card') {
    return (
      <svg {...common}>
        <rect x="3" y="6" width="18" height="12" rx="2.2" />
        <path d="M7 10.5h3.5M7 14h7.5" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path d="M12 4.2l8.4 15H3.6z" />
      <path d="M12 10v3.6M12 16.2v.1" />
    </svg>
  );
}

function Fact({ kind, label, value, detail }: { kind: GlyphKind; label: string; value: ReactNode; detail: string }) {
  return (
    <div className="lcr-fact">
      <span className={kind === 'warn' ? 'lcr-ic warn' : 'lcr-ic'}>
        <Glyph kind={kind} />
      </span>
      <div>
        <div className="lcr-fk">{label}</div>
        <div className="lcr-fv">{value}</div>
        <div className="lcr-fs">{detail}</div>
      </div>
    </div>
  );
}

/* ── One of the six non-report outcomes ───────────────────────────────── */
function ResultPanel({ state }: { state: LiveCheckStateData }) {
  const navigate = useNavigate();
  return (
    <div className="lc">
      <h1 className="lc-h1">Live Check</h1>
      <div className="lc-card lc-result" aria-live="polite">
        <div className="lc-result-head">
          <h2>{state.headline}</h2>
          <span className={`lc-tag ${state.badgeTone === 'warn' ? 'lc-tag-warn' : 'lc-tag-no'}`}>{state.badge}</span>
        </div>
        <p className="lc-result-expl">{state.explanation}</p>
        <dl className="lc-detail">
          {state.detail.map((d) => (<Fragment key={d.label}><dt>{d.label}</dt><dd>{d.value}</dd></Fragment>))}
        </dl>
        <div className="lc-actions">
          {state.actions.map((a) => (
            <button
              key={a.label}
              className={a.variant === 'primary' ? 'lc-btn-primary' : 'lc-btn'}
              onClick={() => {
                if (a.focusField) void navigate(`/live-check?focus=${a.focusField}`);
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── The printed report (portaled, revealed only in print) ─────────────── */
function PrintReport() {
  const quoted = r.quoted;
  const t = quoteTotals(quoted);
  const left = r.annualMax.left - t.insurance;
  return createPortal(
    <div id="lc-print-portal" className="lc-print-portal">
      <div className="lc-print">
        <header>
          <h1>Insurance eligibility report</h1>
          <div className="lc-print-meta">Alamo Heights Dental · San Antonio, TX · Prepared by ELVA · {r.checkedAt} · snapshot {r.snapshotId}</div>
        </header>
        <section>
          <h2>Patient and plan</h2>
          <p><strong>{r.patientName}</strong> · {r.demographics} · {r.isPatient ? 'patient here' : 'not a patient here yet'}</p>
          <p>{r.coverage.plan} · {r.coverage.effective}</p>
          <p>Member {r.member.id} · {r.member.line}</p>
        </section>
        <section>
          <h2>Benefits</h2>
          <p>Annual maximum {formatCurrency(r.annualMax.max)} · used {formatCurrency(r.annualMax.used)} · {formatCurrency(r.annualMax.left)} left. Deductible {r.deductible.line1}; {r.deductible.line2}.</p>
        </section>
        <section>
          <h2>The procedures you asked about</h2>
          <table>
            <thead><tr><th>Procedure</th><th>Covered</th><th>Office fee</th><th>Allowed</th><th>Plan pays</th><th>Patient pays</th></tr></thead>
            <tbody>
              {quoted.map((q) => (
                <tr key={q.code}>
                  <td>{q.code} {q.name}<br />{q.limits}</td><td>{q.covered}</td><td>{formatCents(q.officeFee)}</td><td>{formatCents(q.allowed)}</td><td>{formatCents(q.insurance)}</td><td>{formatCents(patientOwes(q))}</td>
                </tr>
              ))}
              <tr><td>Total</td><td /><td>{formatCents(t.fee)}</td><td>{formatCents(t.allowed)}</td><td>{formatCents(t.insurance)}</td><td>{formatCents(t.patient)}</td></tr>
            </tbody>
          </table>
          <p>These {countWord(quoted.length)} would use {formatCents(t.insurance)} of the {formatCents(r.annualMax.left)} left on the annual maximum, leaving {formatCents(left)}. An estimate, not a guarantee of payment.</p>
        </section>
        <section>
          <h2>What the plan covers</h2>
          <table>
            <thead><tr><th>Service</th><th>Covered</th><th>Rules</th><th>For {firstName(r.patientName)}</th></tr></thead>
            <tbody>
              {r.coverageRows.map((c, i) => (<tr key={i}><td>{c.service}{c.subLabel ? ` — ${c.subLabel}` : ''}</td><td>{c.covered}</td><td>{c.rules}</td><td>{c.forPatient}</td></tr>))}
            </tbody>
          </table>
        </section>
        <section>
          <h2>This payer&rsquo;s rules</h2>
          <table>
            <thead><tr><th>Rule</th><th>What it means</th><th>How ELVA knows</th></tr></thead>
            <tbody>{r.rules.map((rl, i) => (<tr key={i}><td>{rl.rule}</td><td>{rl.meaning}</td><td>{rl.howKnown}</td></tr>))}</tbody>
          </table>
        </section>
        <section>
          <h2>How ELVA verified this</h2>
          <table>
            <thead><tr><th>Source</th><th>When</th><th>Reference</th></tr></thead>
            <tbody>{r.sources.map((s) => (<tr key={s.source}><td>{SOURCE_LABEL[s.source]}</td><td>{s.when}</td><td>{s.reference}</td></tr>))}</tbody>
          </table>
        </section>
        <div className="lc-print-foot">{r.storedNote} · Alamo Heights Dental · {r.patientName} · snapshot {r.snapshotId}</div>
      </div>
    </div>,
    document.body,
  );
}

/* ── The full report (good answer), rcm-29 ─────────────────────────────── */
function ReportView({ inactive }: { inactive: boolean }) {
  const navigate = useNavigate();
  const quoted = inactive ? [] : r.quoted;
  const inactiveState = fx.states.find((s) => s.kind === 'terminated');
  const t = quoteTotals(quoted);
  const first = firstName(r.patientName);
  const writeOff = t.fee - t.allowed;
  const left = r.annualMax.left - t.insurance;
  const sourcesUsed = r.sources.filter((s) => s.when !== '—').length;
  const printNow = () => { window.print(); };

  return (
    <div className="lcr">
      <CompactBand
        mint={!inactive && quoted.length > 0}
        headline={
          inactive ? (
            <>{r.patientName}&rsquo;s plan is <em>not active</em>.</>
          ) : quoted.length > 0 ? (
            <>
              {r.patientName}&rsquo;s plan is {r.coverage.status.toLowerCase()}. The {countWord(quoted.length)} procedures would leave {first} paying <em>{formatCents(t.patient)}</em>.
            </>
          ) : (
            <>{r.patientName}&rsquo;s plan is {r.coverage.status.toLowerCase()}. Nothing was priced on this check.</>
          )
        }
        right={
          <>
            <span>Snapshot <span className="lcr-m">{r.snapshotId}</span></span>
            <BandButton variant="g" onClick={() => { void navigate('/live-check'); }}>Re-check</BandButton>
            <BandButton variant="g" onClick={printNow}>Print this report</BandButton>
          </>
        }
      />

      <div className="lcr-title">
        <div>
          <div className="lcr-lab">Live check › Result</div>
          <h1 className="lcr-name">{r.patientName}</h1>
          <p className="lcr-sub">
            {r.demographics} · <b className={r.isPatient ? '' : 'lcr-amber'}>{r.isPatient ? 'patient here' : 'not a patient here yet'}</b>
          </p>
        </div>
        <div className="lcr-acts">
          <button type="button" className="lcr-btn">Attach to a patient</button>
          <button type="button" className="lcr-btn-teal" onClick={printNow}>Print this report</button>
        </div>
      </div>

      <div className="lcr-facts">
        <Fact
          kind="shield"
          label="Coverage"
          value={inactive ? <span className="lcr-badv">Not active</span> : <span className="lcr-okv">{r.coverage.status}</span>}
          detail={`${r.coverage.plan} · ${r.coverage.effective}`}
        />
        <Fact
          kind="dial"
          label="Annual maximum"
          value={`${formatCurrency(r.annualMax.left)} left`}
          detail={`${formatCurrency(r.annualMax.used)} used of ${formatCurrency(r.annualMax.max)} · resets ${formatDate(r.annualMax.resets)}`}
        />
        <Fact kind="layers" label="Deductible" value={r.deductible.line1} detail={r.deductible.line2} />
        <Fact kind="card" label="Member" value={<span className="lcr-m lcr-small">{r.member.id}</span>} detail={r.member.line} />
      </div>

      {inactive && inactiveState ? (
        <section className="lcr-card amb">
          <div className="lcr-ch">
            <h2>{inactiveState.headline}</h2>
            <span className="lcr-chsub">{inactiveState.badge}</span>
          </div>
          <p className="lcr-lead">{inactiveState.explanation}</p>
          <div className="lcr-acts lcr-acts-left">
            {inactiveState.actions.map((a) => (
              <button
                key={a.label}
                type="button"
                className={a.variant === 'primary' ? 'lcr-btn-teal' : 'lcr-btn'}
                onClick={a.label === 'Print this result' ? printNow : a.label === 'Check a different plan' ? () => { void navigate('/live-check'); } : undefined}
              >
                {a.label}
              </button>
            ))}
          </div>
        </section>
      ) : (
      <section className="lcr-card">
        <div className="lcr-ch">
          <h2>What {first} pays</h2>
          <span className="lcr-chsub">{r.checkedAt} · from {sourcesUsed} sources</span>
        </div>
        {quoted.length > 0 ? (
          <div className="lcr-hero">
            <div>
              <span className="lcr-hero-lab">If all {countWord(quoted.length)} are done today, {first} pays</span>
              <span className="lcr-hero-val">{formatCents(t.patient)}</span>
            </div>
            <div className="lcr-eq">
              <div><span className="lcr-cl">Allowed</span><span className="lcr-cv">{formatCents(t.allowed)}</span></div>
              <span className="lcr-o">&minus;</span>
              <div><span className="lcr-cl">Plan pays</span><span className="lcr-cv">{formatCents(t.insurance)}</span></div>
              <span className="lcr-o">=</span>
              <div><span className="lcr-cl">{first} pays</span><span className="lcr-cv">{formatCents(t.patient)}</span></div>
            </div>
            <p className="lcr-hero-sf">
              Your office fees total {formatCents(t.fee)}; {formatCents(writeOff)} of that is the in-network write-off and cannot be billed to the patient.
            </p>
          </div>
        ) : (
          <p className="lcr-hero-none">Nothing was priced on this check.</p>
        )}
      </section>
      )}

      {quoted.length > 0 ? (
        <section className="lcr-card">
          <div className="lcr-ch">
            <h2>The {countWord(quoted.length)} procedures you asked about</h2>
            <span className="lcr-chsub">Priced against this plan, today</span>
          </div>
          <p className="lcr-lead">
            Office fees are yours. <b>Allowed</b> is the contracted rate &mdash; it is the most {first} can be charged for an in-network service, whatever the office fee is.
          </p>
          <div className="lcr-tbl-wrap">
            <table className="lcr-pt">
              <thead>
                <tr>
                  <th>Procedure</th>
                  <th className="lcr-cvh">Covered</th>
                  <th className="lcr-num">Office fee</th>
                  <th className="lcr-num">Allowed</th>
                  <th className="lcr-num">Plan pays</th>
                  <th className="lcr-pay">{first} pays</th>
                </tr>
              </thead>
              <tbody>
                {quoted.map((q: QuotedProcedure) => {
                  const owes = patientOwes(q);
                  return (
                    <tr key={q.code}>
                      <td className="lcr-pn">
                        <b><span className="lcr-cd">{q.code}</span>{q.name}</b>
                        <span className="lcr-no">{q.limits} <span className="lcr-src">· {sourceText(q.source)}</span></span>
                      </td>
                      <td className="lcr-cvc"><span className={coverClass(q.covered)}>{q.covered}</span></td>
                      <td className="lcr-num lcr-dim">{formatCents(q.officeFee)}</td>
                      <td className="lcr-num lcr-dim">{formatCents(q.allowed)}</td>
                      <td className="lcr-num">{formatCents(q.insurance)}</td>
                      <td className={owes === 0 ? 'lcr-pay lcr-zero' : 'lcr-pay'}>{formatCents(owes)}</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td className="lcr-pn"><b>All {countWord(quoted.length)}, if done today</b></td>
                  <td />
                  <td className="lcr-num lcr-dim">{formatCents(t.fee)}</td>
                  <td className="lcr-num lcr-dim">{formatCents(t.allowed)}</td>
                  <td className="lcr-num">{formatCents(t.insurance)}</td>
                  <td className="lcr-pay lcr-pay-total">{formatCents(t.patient)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="lcr-note">
            These {countWord(quoted.length)} would use <b>{formatCents(t.insurance)}</b> of the {formatCents(r.annualMax.left)} left on the annual maximum, leaving <b>{formatCents(left)}</b>. An estimate, not a guarantee of payment.
          </p>
        </section>
      ) : null}

      {inactive ? null : (
      <section className="lcr-card amb">
        <div className="lcr-ch">
          <h2>What is not known</h2>
          <span className="lcr-chsub">{r.unknown.length} {r.unknown.length === 1 ? 'gap' : 'gaps'}</span>
        </div>
        {r.noHistory ? (
          <p className="lcr-lead">
            {first} has no history at your office, so some things cannot be known. Frequencies, last-done dates and how much of an allowance is already used normally come from your own claims. For someone new, ELVA shows only what the payer reports &mdash; and says so rather than assuming the service is available.
          </p>
        ) : null}
        <div className="lcr-miss-list">
          {r.unknown.map((u) => (
            <div key={u.title} className="lcr-miss">
              <span className="lcr-ic warn"><Glyph kind="warn" /></span>
              <div>
                <b>{u.title}</b>
                <p>
                  {u.detail}
                  {u.source ? <span className="lcr-srcl"> {SOURCE_LABEL[u.source]}</span> : null}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>
      )}

      <section className="lcr-card">
        <div className="lcr-ch">
          <h2>What the plan covers</h2>
          <span className="lcr-chsub">{r.coverageRows.length} service classes</span>
        </div>
        <div className="lcr-tbl-wrap">
          <table className="lcr-rt">
            <thead>
              <tr><th style={{ width: 230 }}>Service</th><th style={{ width: 120 }}>Covered</th><th>Rules</th><th style={{ width: 180 }}>For {first}</th><th style={{ width: 170 }}>Source</th></tr>
            </thead>
            <tbody>
              {r.coverageRows.map((c, i) => (
                <tr key={i}>
                  <td><b>{c.service}</b>{c.subLabel ? <span className="lcr-q">{c.subLabel}</span> : null}</td>
                  <td>{c.covered}</td>
                  <td>{c.rules}</td>
                  <td>{c.forPatient}</td>
                  <td className="lcr-srcl">{sourceText(c.sources)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="lcr-card">
        <div className="lcr-ch">
          <h2>This payer&rsquo;s rules that affect the quote</h2>
          <span className="lcr-chsub">{r.rules.length} of {r.rulesTotal} apply here</span>
        </div>
        <div className="lcr-tbl-wrap">
          <table className="lcr-rt">
            <thead>
              <tr><th style={{ width: 270 }}>Rule</th><th>What it means here</th><th style={{ width: 300 }}>How ELVA knows</th></tr>
            </thead>
            <tbody>
              {r.rules.map((rl, i) => (
                <tr key={i}>
                  <td>
                    <b>{rl.rule}</b>
                    {rl.badge === 'needs_clarification' ? <span className="lcr-chip">Needs an answer</span> : null}
                  </td>
                  <td>{rl.meaning}</td>
                  <td className="lcr-srcl">{rl.howKnown}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="lcr-note">
          {r.rulesLink.split(': ')[0]}:{' '}
          <Link className="lcr-link" to={payerPathFor((r.coverage.plan.split(' · ')[0] ?? ''))}>
            {r.rulesLink.split(': ').slice(1).join(': ')}
          </Link>
        </p>
      </section>

      <section className="lcr-card">
        <div className="lcr-ch">
          <h2>How ELVA verified this</h2>
          <span className="lcr-chsub">{sourcesUsed} of {r.sources.length} sources used</span>
        </div>
        <div className="lcr-tbl-wrap">
          <table className="lcr-rt">
            <thead>
              <tr><th style={{ width: 190 }}>Source</th><th style={{ width: 190 }}>When</th><th>What it gave us</th><th style={{ width: 290 }}>Reference</th></tr>
            </thead>
            <tbody>
              {r.sources.map((s) => (
                <tr key={s.source}>
                  <td><b>{SOURCE_LABEL[s.source]}</b></td>
                  <td className="lcr-q">{s.when}</td>
                  <td>{s.gave}</td>
                  <td className="lcr-srcl">{s.reference}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="lcr-note">{r.storedNote}</p>
      </section>

      <PrintReport />
    </div>
  );
}

/* ── Loading skeleton (rcm-29 §5): band line, four fact cards, one hero card ── */
function ReportSkeleton() {
  return (
    <div className="lcr" aria-busy="true" aria-label="Loading the result">
      <CompactBand loading right={null} />
      <div className="lcr-facts">
        {[0, 1, 2, 3].map((i) => (<div key={i} className="lcr-fact lcr-sk-fact"><span className="lcr-sk lcr-sk-ic" /><div className="lcr-sk-col"><span className="lcr-sk lcr-sk-l" /><span className="lcr-sk lcr-sk-v" /><span className="lcr-sk lcr-sk-s" /></div></div>))}
      </div>
      <section className="lcr-card"><span className="lcr-sk lcr-sk-h" /><span className="lcr-sk lcr-sk-hero" /></section>
    </div>
  );
}

export function LiveCheckResult() {
  const [params] = useSearchParams();
  const stateKind = params.get('state');
  const view = params.get('view');
  const state = stateKind ? fx.states.find((s) => s.kind === stateKind) : undefined;
  // A short simulated fetch so the skeleton is real; ?view=loading holds it for review.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => { setReady(true); }, 400);
    return () => { clearTimeout(t); };
  }, []);
  if (state) return <ResultPanel state={state} />;
  if (!ready || view === 'loading') return <ReportSkeleton />;
  return <ReportView inactive={view === 'inactive'} />;
}
